import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { SyncContextType } from '../types';

const SyncContext = createContext<SyncContextType | undefined>(undefined);

export const useSync = () => {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSync must be used within a SyncProvider');
  }
  return context;
};

interface SyncProviderProps {
  children: React.ReactNode;
  audioElements: Map<number, HTMLAudioElement>;
}

export const SyncProvider: React.FC<SyncProviderProps> = ({ children, audioElements }) => {
  const [syncPoints, setSyncPoints] = useState<Map<number, number>>(new Map());
  const [hasSyncPoints, setHasSyncPoints] = useState(false);
  const [hasJumpedToSyncPoints, setHasJumpedToSyncPoints] = useState(false);

  const setSyncPoint = useCallback((playerNumber: number, time: number) => {
    setSyncPoints(prev => {
      const newMap = new Map(prev);
      newMap.set(playerNumber, time);
      return newMap;
    });
    setHasSyncPoints(true);
  }, []);

  const removeSyncPoint = useCallback((playerNumber: number) => {
    setSyncPoints(prev => {
      const newMap = new Map(prev);
      newMap.delete(playerNumber);
      const stillHasPoints = newMap.size > 0;
      setHasSyncPoints(stillHasPoints);
      if (!stillHasPoints) {
        setHasJumpedToSyncPoints(false);
      }
      return newMap;
    });
  }, []);

  const toggleSyncPoint = useCallback((playerNumber: number, time: number) => {
    setSyncPoints(prev => {
      const newMap = new Map(prev);
      if (newMap.has(playerNumber)) {
        // Remove the sync point if it exists
        newMap.delete(playerNumber);
        const stillHasPoints = newMap.size > 0;
        setHasSyncPoints(stillHasPoints);
        if (!stillHasPoints) {
          setHasJumpedToSyncPoints(false);
        }
      } else {
        // Add the sync point if it doesn't exist
        newMap.set(playerNumber, time);
        setHasSyncPoints(true);
      }
      return newMap;
    });
  }, []);

  const clearSyncPoints = useCallback(() => {
    setSyncPoints(new Map());
    setHasSyncPoints(false);
    setHasJumpedToSyncPoints(false);
  }, []);

  const jumpToSyncPoints = useCallback(() => {
    audioElements.forEach((audio, playerNumber) => {
      const syncPoint = syncPoints.get(playerNumber);
      if (syncPoint !== undefined) {
        audio.currentTime = syncPoint;
      }
    });
    setHasJumpedToSyncPoints(true);
  }, [audioElements, syncPoints]);

  const value = useMemo(() => ({
    syncPoints,
    hasSyncPoints,
    hasJumpedToSyncPoints,
    setSyncPoint,
    removeSyncPoint,
    toggleSyncPoint,
    clearSyncPoints,
    jumpToSyncPoints
  }), [syncPoints, hasSyncPoints, hasJumpedToSyncPoints, setSyncPoint, removeSyncPoint, toggleSyncPoint, clearSyncPoints, jumpToSyncPoints]);

  return (
    <SyncContext.Provider value={value}>
      {children}
    </SyncContext.Provider>
  );
}; 